import { useState, useEffect } from "react";
import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Button,
  Tabs,
  Tab,
  Chip,
  Card,
  CardBody,
  Badge,
  Tooltip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Checkbox,
  CheckboxGroup,
  Divider,
} from "@heroui/react";
import {
  IconCalendar,
  IconUser,
  IconMapPin,
  IconCurrencyDollar,
  IconChalkboard,
  IconCalendarX,
  IconUsers,
} from "@tabler/icons-react";
import Loader from "@/components/loader";
import toast from "react-hot-toast";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { isInNext24Hours } from "@/utils/isInNext24Hours";
import { useLocale, useTranslations } from "../../../../i18n";
import { formatDateWithLocale } from "@/utils/format-date-with-locale";
import { api } from "@/lib/api";
import { useStudioStore } from "@/store/studio-store";
import { useAuthStore } from "@/store/auth-store";

export default function ProfileBookingsTabs({
  onRefresh,
  isActive,
}: {
  onRefresh?: () => void;
  isActive: boolean;
}) {
  const organization = useStudioStore((state) => state.studio);
  const locale = useLocale();
  const t = useTranslations("profile.bookingsPage");
  const { user } = useAuthStore(); // refreshUser TODO

  const [selectedTab, setSelectedTab] = useState("upcoming");
  const [bookings, setBookings] = useState<any[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isParticipantModalOpen, setIsParticipantModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>(
    [],
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);
  const [nowTs, setNowTs] = useState(() => Date.now());

  useEffect(() => {
    // Check for mobile view
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    // Initial check
    checkMobileView();

    // Add resize listener
    window.addEventListener("resize", checkMobileView);

    return () => {
      window.removeEventListener("resize", checkMobileView);
    };
  }, [isActive]);

  const fetchBookings = async () => {
    try {
      if (!organization) return;

      if (!user) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const data = await api("/api/widget/profile/bookings");
      setBookings(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isActive]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNowTs(Date.now());
    }, 30_000);

    return () => clearInterval(interval);
  }, []);

  const handleCancelClick = (booking: any) => {
    setSelectedBooking(booking);

    const totalParticipants = 1 + (booking.participants?.length || 0);

    if (totalParticipants > 1) {
      // Multiple participants - show selection modal
      setSelectedParticipants([]);
      setIsParticipantModalOpen(true);
    } else {
      // Single participant - proceed with full cancellation
      setIsDeleteDialogOpen(true);
    }
  };

  const handleCancel = async (participantIds?: string[]) => {
    try {
      if (!selectedBooking || !selectedBooking.event) {
        toast.error(t("errors.cannotCancel"));
        return;
      }

      if (!organization?.id) {
        throw new Error("Organization not found");
      }

      setIsDeleting(true);

      const requestBody: any = {
        bookingId: selectedBooking.id,
      };

      if (participantIds && participantIds.length > 0) {
        requestBody.participantIds = participantIds;
      }

      const result = await api(`/api/widget/bookings/cancel`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      setIsDeleteDialogOpen(false);
      setIsParticipantModalOpen(false);

      if (result.remainingParticipants > 0) {
        toast.success(
          t("success.participantsCancelled", {
            count: result.participantsCanceled,
          }),
        );
      } else {
        toast.success(t("success.bookingCancelled"));
      }

      await fetchBookings();

      // refreshUser?.(); // TODO

      onRefresh?.();
    } catch (error) {
      toast.error(t("errors.failedToCancel"));
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePartialCancel = () => {
    if (selectedParticipants.length === 0) {
      toast.error(t("errors.selectParticipants"));
      return;
    }

    handleCancel(selectedParticipants);
  };

  const filteredBookings = bookings
    .filter((booking) => {
      switch (selectedTab) {
        case "upcoming":
          return booking.status === "CONFIRMED";
        case "completed":
          return booking.status === "COMPLETED";
        case "cancelled":
          return booking.status === "CANCELED";
        default:
          return true;
      }
    })
    .sort(
      (a, b) =>
        new Date(a.event!.startTime).getTime() -
        new Date(b.event!.startTime).getTime(),
    );

  // Function to get activity name (class or workshop)
  const getActivityName = (booking: any) => {
    if (booking.event?.eventType === "WORKSHOP" && booking.event.workshop) {
      return booking.event.workshop.title;
    } else if (booking.event?.schedule?.class?.name) {
      return booking.event.schedule.class.name;
    }
    return t("unknownActivity");
  };

  // Function to get instructor name
  const getInstructorName = (booking: any) => {
    if (booking.event?.eventType === "WORKSHOP" && booking.event.workshop) {
      return (
        booking.event.workshop.instructor ||
        booking.event.teacherProfile?.user?.name ||
        t("noInstructor")
      );
    }

    return (
      booking.event?.schedule?.teacherProfile?.user?.name || t("noInstructor")
    );
  };

  // Function to get location
  const getLocation = (booking: any) => {
    if (booking.event?.eventType === "WORKSHOP" && booking.event.workshop) {
      if (booking.event.workshop.studio) {
        return booking.event.workshop.studio.name;
      }
      return booking.event.workshop.location || t("noLocation");
    }

    if (booking.event?.room) {
      return booking.event.room;
    }

    return (
      booking.event?.studio?.name ||
      booking.event?.schedule?.studio?.name ||
      t("noLocation")
    );
  };

  const getParticipantCount = (booking: any) => {
    if (typeof booking.participantCount === "number") {
      return booking.participantCount;
    }

    return booking.participants?.length ?? 0;
  };

  const isNewBooking = (booking: any) => {
    if (!booking.createdAt) return false;
    return nowTs - new Date(booking.createdAt).getTime() < 2 * 60 * 1000;
  };

  // Mobile card view for bookings
  const renderBookingCards = (
    bookings: any[],
    showActions: boolean,
    showInvoice: boolean,
  ) => {
    if (bookings.length === 0) {
      return (
        <div className="text-center py-6 text-gray-500">
          <IconCalendarX size={48} className="mx-auto text-gray-400 mb-4" />
          <span className="font-bold text-gray-500">
            {t("noBookingsFound")}
          </span>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {bookings.map((booking) => {
          const isWorkshop = booking.event?.eventType === "WORKSHOP";
          const totalParticipants = getParticipantCount(booking);
          const guestCount = Math.max(totalParticipants - 1, 0);

          return (
            <>
              <Card key={booking.id} className="w-full" shadow="none">
                <CardBody className="p-4">
                  <div className="flex flex-col space-y-3">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-primary">
                          {getActivityName(booking)}
                        </h3>
                      </div>

                      {booking.status === "CANCELED" && (
                        <Chip size="sm" variant="bordered">
                          {t("status.cancelled")}
                        </Chip>
                      )}

                      {(showActions || showInvoice) &&
                        booking.status !== "CANCELED" && (
                          <div className="flex justify-end pt-2">
                            {showActions && booking.status === "CONFIRMED" && (
                              <Button
                                size="sm"
                                color="danger"
                                variant="light"
                                onPress={() => handleCancelClick(booking)}
                              >
                                {totalParticipants > 1
                                  ? t("actions.manageBooking")
                                  : t("actions.cancel")}
                              </Button>
                            )}
                          </div>
                        )}
                    </div>

                    {isWorkshop && (
                      <Badge color="secondary" size="sm">
                        {t("workshop")}
                      </Badge>
                    )}

                    <div className="flex items-center text-sm text-gray-500 gap-2">
                      <IconCalendar size={16} />
                      <span>
                        {booking.event?.startTime
                          ? formatDateWithLocale(
                              new Date(booking.event.startTime),
                              "dd/MM/yyyy HH:mm",
                              locale,
                            )
                          : ""}
                      </span>
                    </div>

                    <div className="flex items-center text-sm text-gray-500 gap-2">
                      {isWorkshop ? (
                        <IconChalkboard size={16} className="text-purple-500" />
                      ) : (
                        <IconUser size={16} />
                      )}
                      <span>{getInstructorName(booking)}</span>
                    </div>

                    <div className="flex items-center text-sm text-gray-500 gap-2">
                      <IconMapPin size={16} />
                      <span>{getLocation(booking)}</span>
                    </div>

                    {/* Participants info */}
                    {totalParticipants > 1 && (
                      <div className="flex items-center text-sm text-gray-600 gap-2">
                        <IconUsers size={16} />
                        <span>
                          {totalParticipants} {t("participants")}
                        </span>
                        {booking.participants &&
                          booking.participants.length > 0 && (
                            <Tooltip
                              content={
                                <div className="p-2">
                                  <p className="font-semibold mb-1">
                                    {t("participants")}:
                                  </p>
                                  {booking.participants.map((participant) => (
                                    <div
                                      key={participant.id}
                                      className="text-sm"
                                    >
                                      {participant.name}
                                      {participant.isFirstTime && (
                                        <span className="text-green-500 ml-1">
                                          ({t("firstTime")})
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              }
                            >
                              <Chip color="success" variant="flat" size="sm">
                                +{guestCount} {t("participants")}
                              </Chip>
                            </Tooltip>
                          )}
                      </div>
                    )}

                    {isWorkshop && booking.event?.workshop?.price && (
                      <div className="flex items-center text-sm text-gray-500 gap-2">
                        <IconCurrencyDollar size={16} />
                        <span>
                          {booking.event.workshop.price}{" "}
                          {organization?.currency || "CHF"}
                        </span>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>

              <Divider className="my-2" />
            </>
          );
        })}
      </div>
    );
  };

  // Desktop table view
  const renderTable = (
    bookings: any[],
    showActions: boolean,
    showInvoice: boolean,
  ) => {
    if (bookings.length === 0) {
      return (
        <div className="text-center py-6 text-gray-500">
          <IconCalendarX size={48} className="mx-auto text-gray-400 mb-4" />
          <span className="font-bold text-gray-500">
            {t("noBookingsFound")}
          </span>
        </div>
      );
    }

    if (showActions) {
      return (
        <div className="overflow-x-auto p-2">
          <Table
            isStriped
            aria-label={t("bookingsTableAriaLabel")}
            shadow="none"
          >
            <TableHeader>
              <TableColumn>{t("tableHeaders.class")}</TableColumn>
              {/* <TableColumn>{t("tableHeaders.type")}</TableColumn> */}
              <TableColumn>{t("tableHeaders.date")}</TableColumn>
              <TableColumn>{t("tableHeaders.location")}</TableColumn>
              <TableColumn>{t("tableHeaders.instructor")}</TableColumn>
              {/* <TableColumn>{t("tableHeaders.participants")}</TableColumn> */}
              <TableColumn>{t("tableHeaders.actions")}</TableColumn>
            </TableHeader>
            <TableBody>
              {bookings
                .sort((a, b) => {
                  return (
                    new Date(b.createdAt)?.getTime() -
                    new Date(a.createdAt)?.getTime()
                  );
                })
                .map((booking) => {
                  // const isWorkshop = booking.event?.eventType === "WORKSHOP";
                  const totalParticipants = getParticipantCount(booking);
                  const guestCount = Math.max(totalParticipants - 1, 0);
                  const isNew = isNewBooking(booking);

                  return (
                    <TableRow
                      key={booking.id}
                      className={isNew ? "bg-amber-50" : ""}
                    >
                      <TableCell>{getActivityName(booking)}</TableCell>
                      {/* <TableCell>
                        {isWorkshop ? (
                          <Chip color="secondary" variant="flat">
                            {t("workshop")}
                          </Chip>
                        ) : (
                          <Chip color="primary" variant="flat">
                            {t("class")}
                          </Chip>
                        )}
                      </TableCell> */}
                      <TableCell>
                        <div className="text-sm text-gray-500">
                          {booking.event?.startTime
                            ? formatDateWithLocale(
                                new Date(booking.event.startTime),
                                "dd/MM/yyyy HH:mm",
                                locale,
                              )
                            : ""}
                        </div>
                      </TableCell>
                      <TableCell>{getLocation(booking)}</TableCell>
                      <TableCell>{getInstructorName(booking)}</TableCell>
                      {/* <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{totalParticipants}</span>
                          {guestCount > 0 &&
                            booking.participants &&
                            booking.participants.length > 0 && (
                              <Tooltip
                                content={
                                  <div className="p-2">
                                    <p className="font-semibold mb-1">
                                      {t("participants")}:
                                    </p>
                                    {booking.participants.map((participant) => (
                                      <div
                                        key={participant.id}
                                        className="text-sm"
                                      >
                                        {participant.name}
                                        {participant.isFirstTime && (
                                          <span className="text-green-500 ml-1">
                                            ({t("firstTime")})
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                }
                              >
                                <Chip color="success" variant="flat" size="sm">
                                  +{guestCount}
                                </Chip>
                              </Tooltip>
                            )}
                        </div>
                      </TableCell> */}
                      <TableCell>
                        <div className="flex gap-2">
                          {booking.status === "CONFIRMED" && (
                            <Button
                              size="sm"
                              color="danger"
                              variant="light"
                              onPress={() => handleCancelClick(booking)}
                            >
                              {totalParticipants > 1
                                ? t("actions.manageBooking")
                                : t("actions.cancel")}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
      );
    }

    // Table for completed and cancelled bookings
    return (
      <div className="overflow-x-auto">
        <Table isStriped aria-label={t("bookingsTableAriaLabel")} shadow="sm">
          <TableHeader>
            <TableColumn>{t("tableHeaders.class")}</TableColumn>
            {/* <TableColumn>{t("tableHeaders.type")}</TableColumn> */}
            <TableColumn>{t("tableHeaders.date")}</TableColumn>
            <TableColumn>{t("tableHeaders.instructor")}</TableColumn>
            {/* <TableColumn>{t("tableHeaders.participants")}</TableColumn> */}
            <TableColumn>{t("tableHeaders.status")}</TableColumn>
          </TableHeader>
          <TableBody>
            {bookings.map((booking) => {
              // const isWorkshop = booking.event?.eventType === "WORKSHOP";
              const totalParticipants = getParticipantCount(booking);
              const guestCount = Math.max(totalParticipants - 1, 0);
              const isNew = isNewBooking(booking);

              return (
                <TableRow
                  key={booking.id}
                  className={isNew ? "bg-amber-50" : ""}
                >
                  <TableCell>{getActivityName(booking)}</TableCell>
                  {/* <TableCell>
                    {isWorkshop ? (
                      <Chip color="secondary" variant="flat">
                        {t("workshop")}
                      </Chip>
                    ) : (
                      <Chip color="primary" variant="flat">
                        {t("class")}
                      </Chip>
                    )}
                  </TableCell> */}
                  <TableCell>
                    <div className="text-sm text-gray-500">
                      {booking.event?.startTime
                        ? formatDateWithLocale(
                            new Date(booking.event.startTime),
                            "dd/MM/yyyy HH:mm",
                            locale,
                          )
                        : ""}
                    </div>
                  </TableCell>
                  <TableCell>{getInstructorName(booking)}</TableCell>
                  {/* <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{totalParticipants}</span>
                      {guestCount > 0 &&
                        booking.participants &&
                        booking.participants.length > 0 && (
                          <Badge color="primary" variant="flat" size="sm">
                            +{guestCount}
                          </Badge>
                        )}
                    </div>
                  </TableCell> */}
                  <TableCell>
                    <Chip variant="bordered">
                      {showInvoice
                        ? t("status.completed")
                        : t("status.cancelled")}
                    </Chip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Card className="w-full" shadow="none">
        <CardBody>
          <Tabs
            selectedKey={selectedTab}
            onSelectionChange={(key) => {
              setSelectedTab(key.toString());
            }}
            color="primary"
            variant="underlined"
            size={isMobileView ? "md" : "lg"}
            classNames={{
              tabList: "overflow-x-auto",
              tab: "whitespace-nowrap",
            }}
          >
            <Tab key="upcoming" title={t("tabs.upcoming")}>
              {isMobileView
                ? renderBookingCards(filteredBookings, true, false)
                : renderTable(filteredBookings, true, false)}
            </Tab>
            <Tab key="completed" title={t("tabs.completed")}>
              {isMobileView
                ? renderBookingCards(filteredBookings, false, false)
                : renderTable(filteredBookings, false, true)}
            </Tab>
            <Tab key="cancelled" title={t("tabs.cancelled")}>
              {isMobileView
                ? renderBookingCards(filteredBookings, false, false)
                : renderTable(filteredBookings, false, false)}
            </Tab>
          </Tabs>
        </CardBody>
      </Card>

      {/* Participant Selection Modal */}
      <Modal
        isOpen={isParticipantModalOpen}
        onClose={() => setIsParticipantModalOpen(false)}
        size="2xl"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            {t("participantModal.title")}
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <p className="text-gray-600">
                {t("participantModal.description")}
              </p>

              {selectedBooking && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">
                    {getActivityName(selectedBooking)}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {selectedBooking.event?.startTime
                      ? formatDateWithLocale(
                          new Date(selectedBooking.event.startTime),
                          "EEEE d MMMM yyyy 'à' HH:mm",
                          locale,
                        )
                      : ""}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <h5 className="font-medium">
                  {t("participantModal.selectParticipants")}
                </h5>

                {selectedBooking?.participants &&
                  selectedBooking.participants.length > 0 && (
                    <CheckboxGroup
                      value={selectedParticipants}
                      onValueChange={setSelectedParticipants}
                      orientation="vertical"
                    >
                      {selectedBooking.participants.map((participant) => (
                        <Checkbox
                          key={participant.id}
                          value={participant.id}
                          className="py-1"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {participant.name}
                            </span>
                            {participant.isFirstTime && (
                              <span className="text-sm text-green-600">
                                {t("firstTime")} -{" "}
                                {t("participantModal.noRefundFirstTime")}
                              </span>
                            )}
                          </div>
                        </Checkbox>
                      ))}
                    </CheckboxGroup>
                  )}

                {/* Warning about cancellation policy */}
                {selectedBooking?.event?.startTime && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      {selectedBooking?.event?.eventType === "WORKSHOP"
                        ? t("participantModal.workshopCancellationPolicy")
                        : t("participantModal.classCancellationPolicy")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="default"
              variant="light"
              onPress={() => setIsParticipantModalOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button color="danger" onPress={() => setIsDeleteDialogOpen(true)}>
              {t("participantModal.cancelEntireBooking")}
            </Button>
            <Button
              color="warning"
              onPress={handlePartialCancel}
              isDisabled={selectedParticipants.length === 0}
              isLoading={isDeleting}
            >
              {t("participantModal.cancelSelected")} (
              {selectedParticipants.length})
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Full Cancellation Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setIsParticipantModalOpen(false);
        }}
        onConfirm={() => handleCancel()}
        confirmLabel={t("cancelDialog.confirmLabel")}
        title={t("cancelDialog.title")}
        message={
          selectedBooking?.event?.eventType === "WORKSHOP"
            ? t("cancelDialog.workshopMessage")
            : t("cancelDialog.message")
        }
        warning={
          selectedBooking?.event?.startTime &&
          (selectedBooking?.event?.eventType === "WORKSHOP"
            ? new Date(selectedBooking.event.startTime).getTime() -
                new Date().getTime() <
              72 * 60 * 60 * 1000
            : isInNext24Hours(selectedBooking?.event?.startTime))
            ? selectedBooking?.event?.eventType === "WORKSHOP"
              ? t("cancelDialog.warningWithin72h")
              : t("cancelDialog.warningWithin24h")
            : ""
        }
        isDeleting={isDeleting}
      />
    </>
  );
}
